/**
 * EmailCampaignsPage.tsx
 *
 * Full email campaign management page ported from PHP.
 * Handles: campaign list with stats/filters/search, 2-step create flow
 * (details then template picker modal), campaign stats view with delivery
 * metrics, click tracking, and daily chart.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Mail,
  Send,
  Clock,
  FileText,
  Search,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Spinner } from '@/components/ui/Spinner';
import type { BadgeVariant } from '@/types/common.types';
import { useToast } from '@/hooks/useToast';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { apiClient } from '@/services/apiClient';
import { FtpTemplatePicker } from '@/components/ftp-editor/FtpTemplatePicker';
import * as campaignsSvc from '@/services/campaigns.service';
import type { Campaign } from '@/services/campaigns.service';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_BADGE_MAP: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  canceled: 'error',
  cancelled: 'error',
};

const FILTER_TABS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sent', label: 'Sent' },
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface CampaignStats {
  total: number;
  draft: number;
  scheduled: number;
  sent: number;
}


// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}


// ─── Create Campaign Modal (Step 1: Details) ────────────────────────────────

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (campaignId: number) => void;
}

function CreateCampaignModal({ isOpen, onClose, onCreated }: CreateCampaignModalProps) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (isOpen) { setName(''); setDescription(''); }
  }, [isOpen]);

  const isValid = name.trim().length > 0;

  async function handleCreate() {
    if (!isValid) return;
    setSaving(true);
    try {
      const result = await campaignsSvc.createCampaign({
        name: name.trim(),
        subject: '',
        description: description.trim() || null,
        status: 'draft',
      });
      toast.success('Campaign created! Now select a template.');
      setName('');
      setDescription('');
      onCreated(result.campaignId);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Email Campaign"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!isValid || saving} onClick={handleCreate}>
            {saving ? <Spinner className="h-4 w-4 mr-2" /> : null}
            Next: Choose Template
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="border-b border-slate-200 pb-4 mb-4">
          <h3 className="text-base font-bold text-slate-800">Email Campaign Details</h3>
        </div>
        <FormField label="Campaign Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., January Newsletter"
          />
        </FormField>
        <FormField label="Description" helpText="For internal reference only \u2014 not visible to recipients">
          <Textarea autoResize
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal notes about this campaign..."
            rows={3}
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export default function EmailCampaignsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // List state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setSearchQuery(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [newCampaignId, setNewCampaignId] = useState<number | null>(null);

  // Fetch campaign list
  const { data: listData, isLoading } = useQuery({
    queryKey: ['campaigns', page, pageSize, statusFilter, searchQuery],
    queryFn: () =>
      campaignsSvc.listCampaigns({
        page,
        pageSize,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const campaigns = listData?.data ?? [];
  const totalItems = (listData as any)?.total ?? listData?.meta?.total ?? 0;

  const { data: statsData } = useQuery({
    queryKey: ['campaigns-stats'],
    queryFn: () => campaignsSvc.getCampaignStats(),
    staleTime: 60_000,
  });

  const summaryStats: CampaignStats = {
    total:     statsData?.total     ?? 0,
    draft:     statsData?.drafts    ?? 0,
    scheduled: statsData?.scheduled ?? 0,
    sent:      statsData?.sent      ?? 0,
  };


  // After create -> open template picker
  function handleCampaignCreated(campaignId: number) {
    setNewCampaignId(campaignId);
    setTemplatePickerOpen(true);
  }

  // Template selected -> save to campaign and redirect to edit
  const templateNavigatedRef = useRef(false);

  function handleTemplateSelected(template: unknown) {
    if (!newCampaignId) return;
    templateNavigatedRef.current = true;
    const tmpl = template as Record<string, string | undefined>;
    const patch: Record<string, string> = {
      html_content: tmpl.html_content || '',
      plain_content: tmpl.plain_content || '',
      // Carry the DnD model so a DnD template opens in the DnD editor.
      json_content: tmpl.json_content || '',
    };
    if (tmpl.subject) patch.subject = tmpl.subject;
    if (tmpl.preheader) patch.preheader = tmpl.preheader;
    if (tmpl.from_name) patch.from_name = tmpl.from_name;
    if (tmpl.from_email) patch.from_email = tmpl.from_email;
    if (tmpl.from_email) patch.reply_to = tmpl.from_email;
    if (tmpl.id) patch.email_template_id = tmpl.id;

    apiClient.patch(`/campaigns/${newCampaignId}`, patch).then(() => {
      toast.success('Template applied!');
    }).catch(() => {
      toast.warning('Template could not be applied, but the campaign was created.');
    }).finally(() => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-stats'] });
      navigate(`/marketing/campaigns/${newCampaignId}`);
    });
  }

  function handleSkipTemplate() {
    if (templateNavigatedRef.current) {
      templateNavigatedRef.current = false;
      return;
    }
    setTemplatePickerOpen(false);
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['campaigns-stats'] });
    if (newCampaignId) navigate(`/marketing/campaigns/${newCampaignId}`);
  }

  function handleRowClick(campaign: Campaign) {
    if (campaign.status === 'draft') {
      navigate(`/marketing/campaigns/${campaign.campaignId}`);
    } else if (campaign.status === 'scheduled') {
      // Scheduled campaigns are locked; only Cancel Send is offered there.
      navigate(`/marketing/campaigns/${campaign.campaignId}/scheduled`);
    } else {
      navigate(`/marketing/campaigns/${campaign.campaignId}/stats`);
    }
  }

  // Table columns
  const columns: DataTableColumn<Campaign>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Campaign',
        render: (row) => (
          <div>
            <div className="font-medium text-slate-900">{row.name}</div>
            {row.subject && (
              <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[300px]">
                {row.subject}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '120px',
        render: (row) => (
          <Badge variant={STATUS_BADGE_MAP[row.status] ?? 'neutral'} dot>
            {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
          </Badge>
        ),
      },
      {
        key: 'totalRecipients',
        header: 'Recipients',
        width: '110px',
        align: 'right' as const,
        render: (row) => (
          <span className="text-sm text-slate-600">
            {formatNumber(row.totalRecipients ?? 0)}
          </span>
        ),
      },
      {
        key: 'sentAt',
        header: 'Scheduled/Sent',
        width: '160px',
        render: (row) => (
          <span className="text-sm text-slate-500">
            {row.status === 'sent'
              ? formatDate(row.sentAt)
              : row.status === 'scheduled'
                ? formatDate(row.scheduledAt)
                : '\u2014'}
          </span>
        ),
      },
    ],
    [],
  );


  // ─── List view ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0 space-y-6">
        <PageHeader
          title="Email Campaigns"
          actions={
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Campaign
            </Button>
          }
        />

        {/* Stats grid */}
        <MetricCardGrid columns={4}>
          <StatCard
            label="Total"
            value={summaryStats.total}
            icon={<Mail className="h-5 w-5" />}
            tone="sky"
          />
          <StatCard
            label="Draft"
            value={summaryStats.draft}
            icon={<FileText className="h-5 w-5" />}
            tone="slate"
          />
          <StatCard
            label="Scheduled"
            value={summaryStats.scheduled}
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />
          <StatCard
            label="Sent"
            value={summaryStats.sent}
            icon={<Send className="h-5 w-5" />}
            tone="emerald"
          />
        </MetricCardGrid>

        {/* Filter tabs + search */}
        <div className="flex items-center justify-between">
          <FilterTabs
            tabs={FILTER_TABS}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search campaigns..."
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="flex-1 min-h-0 mt-6">
        <DataTable<Campaign>
          keyField="campaignId"
          columns={columns}
          data={campaigns}
          loading={isLoading}
          page={page}
          pageSize={pageSize}
          total={totalItems}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          emptyTitle="No campaigns found"
          emptyDescription="Create your first email campaign to get started."
        />
      </div>

      {/* Create campaign modal (step 1) */}
      <CreateCampaignModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCampaignCreated}
      />

      {/* Template picker modal (step 2) */}
      <FtpTemplatePicker
        isOpen={templatePickerOpen}
        onClose={handleSkipTemplate}
        onApply={() => {}}
        mode="select"
        onSelect={handleTemplateSelected}
      />

    </div>
  );
}
